/* Rapporti: come sta andando la raccolta delle ore, persona per persona.
 *
 * E' l'ultima delle sei sezioni promesse dal menu, e arriva per ultima
 * apposta: senza ore vere da misurare non c'e' niente da mostrare qui.
 *
 * **Serve a formare, non a sanzionare**, esattamente come dice il dominio in
 * `RapportoRaccolta`. Il giorno che questi numeri diventano materiale per un
 * rimprovero, la raccolta delle ore peggiora in una settimana: e' scritto nel
 * backend ed e' la ragione per cui questa pagina non ordina le persone per
 * quota in giornata, ne' evidenzia chi sta "peggio". Un ordine per merito
 * sarebbe una classifica, e una classifica e' esattamente il tipo di
 * pressione che il dominio dichiara di voler evitare.
 *
 * **Solo l'amministratore arriva qui**, non chi fattura: la linea non e' il
 * mestiere, e' chi e' titolare e chi e' dipendente (deciso il 20 settembre
 * 2026, vedi RACCOLTA_METRICHE in `core/capacita.py`).
 *
 * **Compare solo chi ha gia' imputato.** Un accesso senza ore non ha ancora
 * niente da dire, e il server non lo elenca: non e' un buco, e' la stessa
 * scelta di "Il catalogo nasce vuoto" applicata alle persone.
 *
 * **Le giornate sospette non sono un giudizio**, sono un controllo di
 * qualita' del dato: piu' di dodici ore in un giorno solo e' quasi sempre un
 * 240 scritto al posto di un 24, non qualcuno che ha davvero lavorato dodici
 * ore.
 */

import { ore } from "../api";
import { useCarica } from "../carica";
import {
  conta,
  dataCompleta,
  oreEMinuti,
  percento,
  type Rapporto,
} from "../dominio";

export function Rapporti() {
  const rapporti = useCarica<Rapporto[]>(() => ore.rapporti(), []);

  return (
    <>

      <p>
        Quante ore sono state imputate in giornata e quante in ritardo,
        persona per persona. Serve a capire dove la raccolta si perde, non a
        giudicare chi imputa.
      </p>

      {rapporti.errore && (
        <p className="errore" role="alert">
          {rapporti.errore}
        </p>
      )}

      {rapporti.dati === undefined ? (
        rapporti.errore ? null : <p className="attesa">Un momento.</p>
      ) : rapporti.dati.length === 0 ? (
        <p className="aiuto">
          Nessuno ha ancora imputato un'ora: finché è così, non c'è niente da
          misurare qui.
        </p>
      ) : (
        <Elenco rapporti={rapporti.dati} />
      )}
    </>
  );
}

function Elenco({ rapporti }: { rapporti: Rapporto[] }) {
  return (
    <section className="elenco">
      <table className="tabella">
        <thead>
          <tr>
            <th>Persona</th>
            <th className="cifra colonna-minuti">Ore</th>
            <th className="cifra">Quota in giornata</th>
            <th className="cifra">In ritardo</th>
          </tr>
        </thead>
        <tbody>
          {rapporti.map((r) => (
            <RigaPersona key={r.persona} r={r} />
          ))}
        </tbody>
      </table>
    </section>
  );
}

function RigaPersona({ r }: { r: Rapporto }) {
  return (
    <>
      <tr>
        <td>
          <span className="pratica-nome">{r.persona_nome}</span>
          <span className="narrativa">
            {conta(r.imputazioni, "imputazione", "imputazioni")}
          </span>
        </td>
        <td className="cifra colonna-minuti">{oreEMinuti(r.minuti)}</td>
        <td className="cifra">
          {r.quota_in_giornata === null ? "ignoto" : percento(r.quota_in_giornata)}
        </td>
        <td className="cifra">{r.in_ritardo}</td>
      </tr>
      {r.senza_data_di_inserimento > 0 && (
        <tr>
          <td colSpan={4}>
            <span className="narrativa">
              {conta(r.senza_data_di_inserimento, "imputazione", "imputazioni")} senza
              data di inserimento, fuori dal conto della quota in giornata.
            </span>
          </td>
        </tr>
      )}
      {r.giornate_sospette.length > 0 && (
        <tr>
          <td colSpan={4}>
            <p className="riga-avviso">
              {conta(r.giornate_sospette.length, "giornata sospetta", "giornate sospette")}
              , più di 12 ore in un giorno solo (probabile refuso, non un
              rimprovero):{" "}
              {r.giornate_sospette
                .map((g) => `${dataCompleta(g.giorno)}, ${oreEMinuti(g.minuti)}`)
                .join("; ")}
              .
            </p>
          </td>
        </tr>
      )}
    </>
  );
}
